// Add-to-calendar link builder — one Google Calendar deep link plus one .ics
// data: URL for everything else (Apple/Outlook/etc.). Both carry the event as
// a UTC instant, so the recipient's calendar shows it in their own zone.
//
// Shared by the addToCalendar content block (components/Blocks.tsx). The
// older per-page copy in CallRegisteredPage.tsx predates this helper and is
// left alone — collapsing it is a separate, behaviour-preserving refactor.

export interface CalendarEvent {
  title: string;
  start: Date;
  durationMinutes: number;
  details?: string;
  location?: string;
  /** Stable UID seed (slug/id). Falls back to the title. */
  uid?: string;
}

export interface CalendarLinks {
  googleHref: string;
  icsHref: string;
}

/** YYYYMMDDTHHMMSSZ stamp for calendar links. */
export function calStamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Returns null when the start instant is invalid — callers render nothing
 *  rather than a broken button. */
export function buildCalendarLinks(ev: CalendarEvent): CalendarLinks | null {
  if (!(ev.start instanceof Date) || Number.isNaN(ev.start.getTime())) return null;
  const minutes = Number.isFinite(ev.durationMinutes) && ev.durationMinutes > 0 ? ev.durationMinutes : 60;
  const end = new Date(ev.start.getTime() + minutes * 60_000);
  const details = ev.details ?? ev.title;
  const location = ev.location ?? 'Online';
  const dates = `${calStamp(ev.start)}/${calStamp(end)}`;
  const googleHref =
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${encodeURIComponent(ev.title)}&dates=${dates}` +
    `&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
  const uidSeed = (ev.uid ?? ev.title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event';
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0',
    'PRODID:-//The Successful Bookkeeper//Engagement Engine//EN',
    'BEGIN:VEVENT',
    `UID:${uidSeed}-${calStamp(ev.start)}@thesuccessfulbookkeeper.com`,
    `DTSTAMP:${calStamp(new Date())}`,
    `DTSTART:${calStamp(ev.start)}`,
    `DTEND:${calStamp(end)}`,
    `SUMMARY:${icsEscape(ev.title)}`,
    `DESCRIPTION:${icsEscape(details)}`,
    `LOCATION:${icsEscape(location)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const icsHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
  return { googleHref, icsHref };
}

/** One Summit-style day for the door page's Google link. */
export interface CalendarDayInput {
  label: string;
  /** UTC ISO instant. */
  date: string;
  durationMinutes: number;
}

const DAY_MS = 24 * 60 * 60_000;

/** Same contract as worker/src/event-calendar.ts isDailySeries: ≥2 days, each
 *  exactly 24h after the last, same duration. Those become ONE recurring
 *  entry (one click adds every day); anything else gets a link per day. */
export function isDailySeries(days: CalendarDayInput[]): boolean {
  if (days.length < 2) return false;
  for (let i = 1; i < days.length; i++) {
    if (new Date(days[i].date).getTime() - new Date(days[i - 1].date).getTime() !== DAY_MS) return false;
    if (days[i].durationMinutes !== days[0].durationMinutes) return false;
  }
  return true;
}

/** Google Calendar links for an event's days: one link when the days form a
 *  daily series (Google's `recur` param), else one per day labelled with the
 *  day. Instants are UTC, so Google shows them in the viewer's own zone. Days
 *  with an invalid date are dropped; an empty result means render no Google
 *  button at all. */
export function googleCalendarLinks(ev: {
  title: string;
  days: CalendarDayInput[];
  details: string;
  location: string;
}): { label: string | null; href: string }[] {
  const days = ev.days
    .filter((d) => Number.isFinite(new Date(d.date).getTime()))
    .map((d) => ({ ...d, durationMinutes: d.durationMinutes > 0 ? d.durationMinutes : 60 }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const href = (title: string, d: CalendarDayInput, recur?: string) => {
    const start = new Date(d.date);
    const end = new Date(start.getTime() + d.durationMinutes * 60_000);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(title)}&dates=${calStamp(start)}/${calStamp(end)}` +
      `&details=${encodeURIComponent(ev.details)}&location=${encodeURIComponent(ev.location)}` +
      (recur ? `&recur=${encodeURIComponent(recur)}` : '');
  };
  if (!days.length) return [];
  if (days.length === 1) return [{ label: null, href: href(ev.title, days[0]) }];
  if (isDailySeries(days)) return [{ label: null, href: href(ev.title, days[0], `RRULE:FREQ=DAILY;COUNT=${days.length}`) }];
  return days.map((d) => ({ label: d.label, href: href(`${ev.title} (${d.label})`, d) }));
}
